<template>
  <validation-observer ref="formObserver" v-slot="{ passes }" tag="div">
    <h2 class="bg-green-500 py-3 px-5 text-white mb-8">
      Payment Information
    </h2>
    <h3>Order Summary</h3>
    <form @submit.prevent="passes(processFormSubmission)">
      <d-field label="Renewal Period">
        <p>{{ startRenewalDate | formatShortDate }} - {{ endRenewalDate | formatShortDate }}</p>
      </d-field>
      <d-field label="Renewal Fee">
        <p>${{ renewalFee.toFixed(2) }}</p>
      </d-field>
      <d-field label="Late Fee">
        <p>${{ lateFee.toFixed(2) }}</p>
      </d-field>
      <d-field label="Total Cost">
        <p>${{ transactionAmount.toFixed(2) }}</p>
      </d-field>

      <h3>Billing Information</h3>
      <div class="flex flex-wrap -mx-5 mb-4 md:mb-0">
        <d-field label="Billing First Name" class="w-full md:w-1/2 px-5">
          <d-input aria-label="first name" v-model="billingFirstName" required />
        </d-field>
        <d-field label="Billing Last Name" class="w-full md:w-1/2 px-5">
          <d-input v-model="billingLastName" aria-label="Last name" required />
        </d-field>
      </div>
      <d-field label="Company Name">
        <d-input v-model="companyName" aria-label="Company name" />
      </d-field>
      <d-field label="Billing Address Line 1">
        <d-input v-model="billingAddressLine1" aria-label="Address Line 1" placeholder="Street Address" required />
      </d-field>
      <d-field label="Billing Address Line 2">
        <d-input v-model="billingAddressLine2" aria-label="Address Line 2" placeholder="suite, apt. or box #" />
      </d-field>
      <div class="flex flex-wrap -mx-5">
        <d-field label="City" class="w-full md:w-1/3 px-5">
          <d-input v-model="billingCity" aria-label="City" required />
        </d-field>
        <d-field label="State/Province" class="w-full md:w-1/3 px-5">
          <d-input v-model="billingState" aria-label="State" required />
        </d-field>
        <d-field label="Zip" class="w-full md:w-1/3 px-5">
          <d-input v-model="billingZip" aria-label="Zip Code" required />
        </d-field>
      </div>
      <d-field label="Phone">
        <d-input v-model="billingPhone" aria-label="Phone Number" required />
      </d-field>
      <d-field label="Email" rules="email">
        <d-input v-model="billingEmail" aria-label="email" required />
      </d-field>

      <h3>Credit Card Payment Information</h3>
      <d-field label="Name on Card">
        <d-input v-model="nameOnCard" aria-label="Name on Card" required />
      </d-field>
      <d-field label="Card Number">
        <d-input v-model="cardNumber" aria-label="Card Number" required type="number" />
      </d-field>
      <d-field label="CVV2">
        <d-input v-model="cardCVV" aria-label="Card CVV number" required type="number" />
      </d-field>
      <d-field label="Card Expiration">
        <div class="flex flex-wrap -mx-5 mb-4 md:mb-0">
          <d-select v-model="cardExpMonth" class="w-full md:w-1/2 px-5" expanded required>
            <option value="" disabled>
              Select a Month
            </option>
            <option v-for="month in monthSelectOptions" :key="month" :value="month">
              {{ month }}
            </option>
          </d-select>
          <d-select v-model="cardExpYear" class="w-full md:w-1/2 px-5" expanded required>
            <option value="" disabled>
              Select a Year
            </option>
            <option v-for="year in yearSelectOptions" :key="year" :value="year">
              {{ year }}
            </option>
          </d-select>
        </div>
      </d-field>
      <d-field label="Comments">
        <d-input v-model="billingComments" aria-label="Comments" type="textarea" />
      </d-field>
      <div class="flex flex-wrap justify-center md:justify-end pt-5">
        <d-btn class="mx-5" @click="goToPrevPage">
          Prev <span class="hidden sm:block sm:ml-1">Step</span>
        </d-btn>
        <d-btn type="submit" class="mx-5 md:mx-0" :loading="submitting">
          Submit
        </d-btn>
      </div>
    </form>
  </validation-observer>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex';
  import { mapFields } from '@resources/scripts/utilities/field.utilities';
  import { RENEWAL_FORM } from '@resources/scripts/store/mutation-types';

  const { mapActions, mapState, mapGetters } = createNamespacedHelpers('renewalForm');

  export default {
    computed: {
      ...mapFields({
        fields: ['startRenewalDate', 'endRenewalDate', 'companyName', 'lateFee', 'renewalFee', 'billingFirstName', 'billingLastName', 'billingAddressLine1', 'billingAddressLine2', 'billingCity', 'billingState', 'billingZip', 'billingPhone', 'billingEmail', 'nameOnCard', 'cardNumber', 'cardCVV', 'cardExpMonth', 'cardExpYear', 'billingComments'],
        module: 'renewalForm',
        base: 'formFields',
        mutation: RENEWAL_FORM.FORM_FIELDS,
      }),
      ...mapState([
        'submitting',
      ]),

      ...mapGetters([
        'transactionAmount',
      ]),

      monthSelectOptions() {
        return ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
      },

      yearSelectOptions() {
        const currentYear = new Date().getFullYear(); const
          years = [];
        let startYear = new Date().getFullYear() - 2;
        while (startYear <= (currentYear + 20)) {
          years.push(startYear++);
        }

        return years;
      },
    },

    methods: {
      ...mapActions([
        'goToPrevPage',
        'submitForm',
      ]),

      async processFormSubmission() {
        try {
          await this.submitForm();
        } catch (error) {
          this.$toast.error('Unable to process your renewal. For further assistance, please contact us for additional support.');
        }
      },
    },
  };
</script>